import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Home } from 'lucide-react';
import PVSPageLayout from '../PVSPageLayout';
import FormSuccessModal from '../FormSuccessModal';
import { lookupVesselBySerial, isVesselScrapped } from '../formTraceabilityService';
import HeaderSection from './sections/HeaderSection';
import TankDetailsSection from './sections/TankDetailsSection';
import ChecklistSection from './sections/ChecklistSection';
import UltrasonicSection from './sections/UltrasonicSection';
import ReliefValveSection from './sections/ReliefValveSection';
import SignOffSection from './sections/SignOffSection';
import MaterialsSection from './sections/MaterialsSection';
import {
  MATERIAL_ROW_COUNT,
  buildEmptyUltrasonic,
  ULTRASONIC_PANEL_KEYS,
  ULTRASONIC_END_PANEL_KEYS,
  ULTRASONIC_ROW_KEYS,
  ULTRASONIC_GRID_LABELS,
  ULTRASONIC_ROW_LABELS,
  ULTRASONIC_CONTROL_TOLERANCE,
  ULTRASONIC_FAILURE_GUIDANCE,
  ULTRASONIC_PANEL_CONTROL_MAP,
  ULTRASONIC_SHELL_KEYS,
  ULTRASONIC_SHELL_LABELS,
  ULTRASONIC_SHELL_PANELS,
  ULTRASONIC_ADDITIONAL_PANEL_KEYS,
  ULTRASONIC_ADDITIONAL_PANEL_CONTROL_MAP,
  ULTRASONIC_ADDITIONAL_GRID_LABELS,
  ULTRASONIC_SHARED_ROW_CAPACITY,
  END_READING_LABEL,
  SHELL_COL_COUNT,
} from './fieldNames';
import { generateCalorCdePdf } from './generatePDF';
import {
  fetchCertificateStatus,
  CERTIFICATE_EXHAUSTED_MESSAGE,
} from './certificateNumberService';
import { resolveManufacturerCode } from './manufacturerCodes';
import { getCurrentUserDisplayName } from '../../utils/currentUser';

const makeEmptyMaterials = () =>
  Array.from({ length: MATERIAL_ROW_COUNT }, () => ({
    code: '',
    description: '',
    quantity: '',
    replenish: '',
  }));

const DEFAULT_DEPOT_NAME = 'Pressure Vessel Services';
const DEFAULT_DEPOT_ADDRESS = 'Pressure Vessel Services LTD, Coley Road, Halifax, HX3 7SA';

const NEXT_EXAM_OFFSET_YEARS = 10;

// Next examination date: always 31 December of (examination year + 10).
// Falls back to the current year when the examination date is empty/invalid.
// Returned in ISO ("YYYY-12-31") so it round-trips through the date logic.
const computeNextExaminationDateIso = (dateStr) => {
  const m = String(dateStr || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const baseYear = m ? Number(m[1]) : new Date().getFullYear();
  return `${String(baseYear + NEXT_EXAM_OFFSET_YEARS).padStart(4, '0')}-12-31`;
};

// Convert an ISO date ("YYYY-MM-DD") to UK display format ("DD/MM/YYYY").
// Returns the input unchanged when it isn't a plain ISO date.
const formatIsoToDDMMYYYY = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || '');
};

const todayIso = new Date().toISOString().split('T')[0];

// Equipment register (PVS-EQP-001) traceability. The inspector selects the
// torque wrench / pressure gauge by reference ID; the matching serial number
// is printed in the certificate comments section.
const TORQUE_WRENCH_SERIALS = {
  T1: '21073435',
  T2: '2112465',
  T3: '21122478',
};
const PRESSURE_GAUGE_SERIALS = {
  P1: '04/8864',
  P2: '04/8865',
  P3: '4/66',
};

const TORQUE_WRENCH_OPTIONS = Object.entries(TORQUE_WRENCH_SERIALS).map(
  ([value, serial]) => ({ value, label: `${value} (Serial No. ${serial})` }),
);
const PRESSURE_GAUGE_OPTIONS = Object.entries(PRESSURE_GAUGE_SERIALS).map(
  ([value, serial]) => ({ value, label: `${value} (Serial No. ${serial})` }),
);

// Build the equipment traceability lines that get appended to the comments.
const buildEquipmentCommentLines = (signOff) => {
  const lines = [];
  const torqueSerial = TORQUE_WRENCH_SERIALS[signOff?.torqueWrench];
  if (torqueSerial) lines.push(`Torque wrench used - Serial No. ${torqueSerial}`);
  const gaugeSerial = PRESSURE_GAUGE_SERIALS[signOff?.pressureGauge];
  if (gaugeSerial) lines.push(`Pressure gauge used - Serial No. ${gaugeSerial}`);
  return lines.join('\n');
};

// True when the depot is Pressure Vessel Services (the only depot that uses the
// auto-generated PV nameplate/test code scheme).
const isPvsDepot = (name) => /pressure\s*vessel\s*services/i.test(String(name || ''));

// WSE reference is derived from the examination type.
const WSE_REFERENCE_BY_EXAM_TYPE = {
  thorough: 'Part 5',
  inService: 'Part 3',
  annual: 'Part 1',
};

// In-Service exams append a suffix letter chosen by vessel type.
// TODO(confirm): mapping of aboveground/underground -> X / V.
const IN_SERVICE_SUFFIX = {
  aboveground: 'X',
  underground: 'V',
};

// Build the PVS nameplate/test code from the examination date and options:
//   PV + <2-digit year> + <month letter A..L> (+ X|V for In-Service)
// Returns null when the depot isn't PVS or the date is unusable, so callers
// can leave the field untouched for manual entry.
const buildNameplateTestCode = ({ depotName, examinationType, vesselType, dateStr }) => {
  if (!isPvsDepot(depotName)) return null;
  const m = String(dateStr || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const yy = m[1].slice(-2);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  const monthLetter = String.fromCharCode(64 + month); // 1 -> A, 12 -> L
  let code = `PV${yy}${monthLetter}`;
  if (examinationType === 'inService') {
    code += IN_SERVICE_SUFFIX[vesselType] || IN_SERVICE_SUFFIX.aboveground;
  }
  return code;
};

// Pull the first signed decimal number out of a free-text pressure string,
// e.g. "17.25 bar" -> 17.25, "17.25 BARG" -> 17.25, "" / "abc" -> NaN.
const parsePressure = (value) => {
  const match = String(value || '').match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : NaN;
};

// Normalise a serial for comparison the same way the backend does, so we can
// tell whether the entered value matched the standard serial or the Calor
// serial number.
const normalizeSerial = (value) =>
  String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Tank and relief-valve text values print onto the certificate in capitals,
// so they are stored upper-cased. Non-text keys (colour radio, use
// checkboxes) are excluded because their values are matched literally.
const TANK_TEXT_KEYS = [
  'serialNumber',
  'nameplateTestCode',
  'typeCode',
  'manufacturersCode',
  'manufacturerName',
  'manufacturersTestDate',
  'previousInserviceDateCode',
  'previousThoroughDateCode',
  'tankSize',
  'designCode',
  'designPressure',
];
const RELIEF_VALVE_TEXT_KEYS = ['manufacturer', 'type', 'setPressure', 'testDates'];

const toUpper = (value) => (typeof value === 'string' ? value.toUpperCase() : value);

// Set pressure is a number: digits plus at most one decimal point. A typed
// comma becomes the decimal point so "17,25" can't silently print as 1725.
const sanitizeSetPressure = (value) => {
  const digitsAndDots = String(value ?? '').replace(/,/g, '.').replace(/[^\d.]/g, '');
  const firstDot = digitsAndDots.indexOf('.');
  if (firstDot === -1) return digitsAndDots;
  return (
    digitsAndDots.slice(0, firstDot + 1)
    + digitsAndDots.slice(firstDot + 1).replace(/\./g, '')
  );
};

// Upper-case every printed text value in the tank / relief-valve sections.
const upperCaseTextFields = (section, keys) => {
  const next = { ...(section || {}) };
  keys.forEach((key) => {
    if (typeof next[key] === 'string') next[key] = next[key].toUpperCase();
  });
  return next;
};

const INITIAL_STATE = {
  header: {
    documentType: 'certificate',
    customerName: DEFAULT_DEPOT_NAME,
    installationAddress: DEFAULT_DEPOT_ADDRESS,
    deliveryPointCode: '',
    certificateNumber: '',
    wseReference: WSE_REFERENCE_BY_EXAM_TYPE.thorough,
    examinationType: 'thorough',
    installationCategory: 'Domestic',
    vesselType: 'aboveground',
    installationUse: 'Vapour',
  },
  tank: {
    serialNumber: '',
    nameplateTestCode: '',
    typeCode: '',
    manufacturersCode: '',
    manufacturerName: '',
    manufacturersTestDate: '',
    previousInserviceDateCode: '',
    previousThoroughDateCode: '',
    tankSize: '',
    colour: '',
    designCode: '',
    designPressure: '',
    useVap: false,
    useTopLiq: false,
    useBottomLiq: false,
  },
  checklist: {
    externalSurfacesExamined: false,
    internalSurfacesExamined: false,
    hydraulicTest: 'no',
    hydraulicTestPressure: '',
    allValvesFittingsExamined: false,
    reliefValvesReplacedRainCaps: false,
    reliefValveAdaptorChecked: false,
    reliefValveAdaptorReplaced: false,
    fixedLiquidLevelChecked: false,
    floatGaugeScrewsChecked: false,
    hoodServiceableLockable: false,
    allConnectionsCheckedLeaks: false,
    vapourOffTakeValveOperational: false,
    acmeFillerValveVisuallyChecked: false,
    newFillerSealingWasherFitted: false,
    manualShutoffChecked: false,
    pressureGaugeChecked: false,
    gaugeCalibrationChecked: false,
    paintworkMeetsRequirements: '',
    paintworkRequiresRepaint: false,
    paintworkRequiresCleaning: false,
    corrosionState: '',
    tankCompoundLabelsSatisfactory: false,
    // Conditionally-shown checklist items (see conditional visibility logic).
    coatingHolidayCheck: false,
    foundationsSatisfactory: false,
    hoseIfFittedExamined: false,
    siteConditionsSatisfactory: false,
  },
  ultrasonic: buildEmptyUltrasonic(),
  reliefValve: {
    manufacturer: '', type: '', setPressure: '', testDates: '',
  },
  signOff: {
    employerName: DEFAULT_DEPOT_NAME,
    inspectorName: '',
    dateExamined: todayIso,
    inspectorCertificateNumber: '',
    inspectorSignature: '',
    additionalExamination: '',
    vesselSatisfactory: '',
    repairsRequired: '',
    changesSafeLimits: '',
    wseSuitable: '',
    vesselNextExaminationDate: computeNextExaminationDateIso(todayIso),
    upliftRequired: 'no',
    torqueWrench: '',
    pressureGauge: '',
    comments: '',
  },
};

// Draft auto-save: the whole form (formData + materials) is persisted to
// localStorage as the inspector works, so navigating away and back — or an
// accidental reload — never loses their progress. The draft is cleared on a
// successful submit or when the inspector presses "Clear Form".
const DRAFT_STORAGE_KEY = 'calorCdeFormDraft';

const clearDraftStorage = () => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const loadDraftState = () => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Merge a persisted formData draft over INITIAL_STATE so newly-added fields
// still get their defaults even if the draft predates them.
const mergeDraftFormData = (draftFormData) => {
  if (!draftFormData || typeof draftFormData !== 'object') return INITIAL_STATE;
  const merged = { ...INITIAL_STATE };
  Object.keys(INITIAL_STATE).forEach((section) => {
    const val = draftFormData[section];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      merged[section] = { ...INITIAL_STATE[section], ...val };
    } else if (val !== undefined) {
      merged[section] = val;
    }
  });
  return merged;
};

const isCalorOwner = (owner) => /calor/i.test(String(owner || ''));

// Placeholder written into the certificate when the inspector confirms there
// are no comments to record.
const NO_COMMENTS_PLACEHOLDER = 'Inspector confirmed no comments';

// Auto-note added to the comments when a second main shell is recorded.
const ADDITIONAL_SHELL_COMMENT =
  'Additional main shell readings recorded (second main shell - Panels 5 & 6).';

// Every angle on a main shell should carry the same number of readings. When
// they don't, the inspector either amends them or explains why -- that
// explanation prints in the comments behind this prefix.
const UNEVEN_READINGS_COMMENT_PREFIX = 'Uneven ultrasonic reading counts: ';

// Short panel names for the uneven-reading messages; the full grid labels are
// too long to read inside a sentence.
const MAIN_SHELL_PANEL_SHORT_LABELS = {
  panel2: 'Front (Panel 2)',
  panel4: 'Back (Panel 4)',
  panel5: 'Front (Panel 5)',
  panel6: 'Back (Panel 6)',
};

// Baseline reading count for a main shell: the count used at the most angles.
// Ties resolve to the higher count so a missed reading is reported as missing
// rather than treated as the norm.
const baselineReadingCount = (counts) => {
  const tally = new Map();
  counts.forEach((count) => tally.set(count, (tally.get(count) || 0) + 1));
  let baseline = 0;
  let bestFrequency = 0;
  tally.forEach((frequency, count) => {
    if (frequency > bestFrequency || (frequency === bestFrequency && count > baseline)) {
      baseline = count;
      bestFrequency = frequency;
    }
  });
  return baseline;
};

const describeReadingCountIssue = ({ angle, count, baseline }) => {
  const diff = count - baseline;
  if (diff > 0) {
    return `You've done ${diff} more reading${diff === 1 ? '' : 's'} at ${angle} than the rest`
      + ` (${count} readings against ${baseline} at the other angles).`;
  }
  const missing = -diff;
  return `You've done ${missing === 1 ? 'one less reading' : `${missing} fewer readings`} at ${angle}`
    + ` (${count} readings against ${baseline} at the other angles).`;
};

// Conditions that legally prevent the document being issued as a certificate.
// When ANY of these is met the CDE must be downgraded to a Report or
// Inspection (no signature; "This is not a certificate" printed on the PDF)
// and the inspector must explain why in the comments.
const getCertificateDisqualifiers = (formData) => {
  const s = formData.signOff || {};
  const c = formData.checklist || {};
  const reasons = [];
  if (s.additionalExamination === 'yes') reasons.push('Additional examination required');
  if (s.repairsRequired === 'yes') reasons.push('Repairs required');
  if (s.changesSafeLimits === 'yes') reasons.push('Changes to safe operating limits');
  if (s.wseSuitable === 'no') reasons.push('WSE not suitable');
  if (c.paintworkMeetsRequirements === 'no') reasons.push('Paintwork does not meet requirements');
  if (c.corrosionState === 'surface') reasons.push('Tank has surface corrosion');
  if (c.corrosionState === 'severe') reasons.push('Tank is severely corroded');
  return reasons;
};

const CalorCDEForm = ({ onBack, onOpenTankCertification, onOpenLineOfContact, tankCertificationUnlocked = true }) => {
  const [formData, setFormData] = useState(() => mergeDraftFormData(loadDraftState()?.formData));
  const [materials, setMaterials] = useState(() => {
    const draftMaterials = loadDraftState()?.materials;
    return Array.isArray(draftMaterials) ? draftMaterials : makeEmptyMaterials();
  });
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [serialLookupState, setSerialLookupState] = useState({ loading: false, notFound: false, lastLookup: '' });
  const [missingVesselFields, setMissingVesselFields] = useState({});
  const [wrongOwnerInfo, setWrongOwnerInfo] = useState(null);
  const [pressureWarningOpen, setPressureWarningOpen] = useState(false);
  const prevExceedsRef = useRef(false);
  const [ultrasonicWarningOpen, setUltrasonicWarningOpen] = useState(false);
  const [commentsWarningOpen, setCommentsWarningOpen] = useState(false);
  const pendingCommentsActionRef = useRef(null);
  // Uneven main-shell reading counts: amend, or explain in the comments.
  const [readingCountsOpen, setReadingCountsOpen] = useState(false);
  const [readingCountsExplanation, setReadingCountsExplanation] = useState('');
  const [readingCountsError, setReadingCountsError] = useState('');
  const pendingReadingCountsActionRef = useRef(null);
  // Certificate-downgrade failsafe modal state.
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [downgradeChoice, setDowngradeChoice] = useState('report');
  const [downgradeExplanation, setDowngradeExplanation] = useState('');
  const [downgradeError, setDowngradeError] = useState('');
  const pendingDowngradeActionRef = useRef(null);
  const [certificateLoading, setCertificateLoading] = useState(true);
  const [certificateBlocked, setCertificateBlocked] = useState(null);
  const [numberingDisabledForTesting, setNumberingDisabledForTesting] = useState(false);
  // Schneider UK/France disambiguation: holds the option list + the name that
  // triggered it while the confirmation modal is open.
  const [schneiderPrompt, setSchneiderPrompt] = useState(null);

  // Pre-fill the inspector name with the logged-in user's display name.  Read
  // on mount (not at module load) so it reflects the current session, and only
  // when the field is still empty so manual edits are preserved.
  useEffect(() => {
    const displayName = getCurrentUserDisplayName();
    if (!displayName) return;
    setFormData((prev) =>
      prev.signOff.inspectorName
        ? prev
        : { ...prev, signOff: { ...prev.signOff, inspectorName: displayName } },
    );
  }, []);

  // Persist the in-progress form to localStorage on every change so exiting
  // the form (or a reload) keeps the content until it's submitted or cleared.
  useEffect(() => {
    try {
      localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({ formData, materials }),
      );
    } catch {
      /* ignore quota / serialization errors */
    }
  }, [formData, materials]);

  // Reset the form to a blank state and wipe the saved draft. The freshly
  // allocated certificate number and the logged-in inspector name are kept so
  // the user doesn't have to reload to get them back.
  const handleClearForm = () => {
    setFormData((prev) => {
      const fresh = { ...INITIAL_STATE };
      fresh.header = {
        ...INITIAL_STATE.header,
        certificateNumber: prev.header.certificateNumber,
      };
      const displayName = getCurrentUserDisplayName();
      if (displayName) {
        fresh.signOff = { ...INITIAL_STATE.signOff, inspectorName: displayName };
      }
      return fresh;
    });
    setMaterials(makeEmptyMaterials());
    setErrorMsg('');
    clearDraftStorage();
    setClearConfirmOpen(false);
  };

  // Auto-generate the nameplate/test code for PVS-depot certificates. Kept in
  // sync with the examination date, examination type and vessel type. When the
  // rule doesn't apply (non-PVS depot / bad date) the field is left as-is for
  // manual entry.
  const depotName = formData.header.customerName;
  const examinationType = formData.header.examinationType;
  const vesselType = formData.header.vesselType;
  const dateExamined = formData.signOff.dateExamined;
  useEffect(() => {
    const code = buildNameplateTestCode({
      depotName,
      examinationType,
      vesselType,
      dateStr: dateExamined,
    });
    if (!code) return;
    setFormData((prev) =>
      prev.tank.nameplateTestCode === code
        ? prev
        : { ...prev, tank: { ...prev.tank, nameplateTestCode: code } },
    );
  }, [depotName, examinationType, vesselType, dateExamined]);

  // Auto-manage the additional-shell note in the comments: add it when the
  // second main shell is enabled, remove it (only the auto line) when disabled.
  const additionalShellEnabled = formData.ultrasonic.additionalShell;
  useEffect(() => {
    setFormData((prev) => {
      const comments = prev.signOff.comments || '';
      const hasLine = comments.includes(ADDITIONAL_SHELL_COMMENT);
      if (additionalShellEnabled && !hasLine) {
        const next = comments.trim()
          ? `${comments.trim()}\n${ADDITIONAL_SHELL_COMMENT}`
          : ADDITIONAL_SHELL_COMMENT;
        return { ...prev, signOff: { ...prev.signOff, comments: next } };
      }
      if (!additionalShellEnabled && hasLine) {
        const next = comments
          .split('\n')
          .filter((line) => line.trim() !== ADDITIONAL_SHELL_COMMENT)
          .join('\n')
          .trim();
        return { ...prev, signOff: { ...prev.signOff, comments: next } };
      }
      return prev;
    });
  }, [additionalShellEnabled]);

  // Keep the WSE reference in sync with the examination type.
  useEffect(() => {
    const wse = WSE_REFERENCE_BY_EXAM_TYPE[examinationType];
    if (!wse) return;
    setFormData((prev) =>
      prev.header.wseReference === wse
        ? prev
        : { ...prev, header: { ...prev.header, wseReference: wse } },
    );
  }, [examinationType]);

  // Visibility rules for the conditional inspection-checklist items.
  const nameplateStartsWithM = String(formData.tank.nameplateTestCode || '')
    .trim().toUpperCase().startsWith('M');
  const isUnderground = formData.header.vesselType === 'underground';
  const isCommercial = formData.header.installationCategory === 'Commercial';
  const conditionalChecklistVisibility = useMemo(() => ({
    coatingHolidayCheck: isUnderground,
    foundationsSatisfactory: nameplateStartsWithM,
    hoseIfFittedExamined: nameplateStartsWithM && isCommercial,
    siteConditionsSatisfactory: nameplateStartsWithM,
  }), [nameplateStartsWithM, isUnderground, isCommercial]);

  // Untick any conditional item that is no longer visible so hidden values
  // never end up on the certificate.
  useEffect(() => {
    setFormData((prev) => {
      let changed = false;
      const checklist = { ...prev.checklist };
      Object.entries(conditionalChecklistVisibility).forEach(([key, visible]) => {
        if (!visible && checklist[key]) {
          checklist[key] = false;
          changed = true;
        }
      });
      return changed ? { ...prev, checklist } : prev;
    });
  }, [conditionalChecklistVisibility]);

  useEffect(() => {
    let cancelled = false;

    const loadCertificateStatus = async () => {
      setCertificateLoading(true);
      try {
        const status = await fetchCertificateStatus();
        if (cancelled) return;

        if (status.numberingDisabled) {
          setNumberingDisabledForTesting(true);
          setCertificateBlocked(null);
          return;
        }

        setNumberingDisabledForTesting(false);

        if (!status.rangeConfigured) {
          setCertificateBlocked('Certificate numbers have not been configured. Please contact management.');
          return;
        }

        if (!status.available || !status.nextNumber) {
          setCertificateBlocked(CERTIFICATE_EXHAUSTED_MESSAGE);
          return;
        }

        setCertificateBlocked(null);
        setFormData((prev) => ({
          ...prev,
          header: { ...prev.header, certificateNumber: status.nextNumber },
        }));
      } catch (error) {
        if (!cancelled) {
          setCertificateBlocked(error.message || 'Could not load certificate number status.');
        }
      } finally {
        if (!cancelled) setCertificateLoading(false);
      }
    };

    loadCertificateStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cross-reference the manufacturer name against the Calor manufacturer-code
  // table and auto-populate Manufacturer's Code.  Runs whenever the name
  // changes (serial pre-fill or manual edit).  Schneider resolves to a modal
  // asking the user to confirm UK vs France so the correct code is used.
  const manufacturerName = formData.tank.manufacturerName;
  useEffect(() => {
    const result = resolveManufacturerCode(manufacturerName);
    if (result.status === 'matched') {
      setSchneiderPrompt(null);
      setFormData((prev) =>
        prev.tank.manufacturersCode === result.code
          ? prev
          : { ...prev, tank: { ...prev.tank, manufacturersCode: result.code } },
      );
    } else if (result.status === 'ambiguous') {
      setSchneiderPrompt({ name: manufacturerName, options: result.options });
    } else {
      setSchneiderPrompt(null);
    }
  }, [manufacturerName]);

  const handleSchneiderChoice = (option) => {
    setFormData((prev) => ({
      ...prev,
      tank: {
        ...prev.tank,
        manufacturersCode: option.code,
        manufacturerName: option.label.toUpperCase(),
      },
    }));
    setSchneiderPrompt(null);
  };

  const updateSection = (section) => (key, value) => {
    let nextValue = value;
    if (section === 'reliefValve' && key === 'setPressure') {
      nextValue = sanitizeSetPressure(value);
    } else if (
      (section === 'tank' && TANK_TEXT_KEYS.includes(key))
      || (section === 'reliefValve' && RELIEF_VALVE_TEXT_KEYS.includes(key))
    ) {
      nextValue = toUpper(value);
    }
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: nextValue },
    }));
    if (section === 'tank' && key === 'serialNumber' && serialLookupState.lastLookup) {
      setSerialLookupState({ loading: false, notFound: false, lastLookup: '' });
      setMissingVesselFields({});
      setWrongOwnerInfo(null);
    }
  };

  const updateMaterial = (rowIdx, key, value) =>
    setMaterials((prev) => prev.map((row, idx) => (idx === rowIdx ? { ...row, [key]: value } : row)));

  // Sign-off setter -- behaves like updateSection('signOff') except that
  // changes to `dateExamined` also re-derive `vesselNextExaminationDate`
  // (`dateExamined + 10y`).  The inspector can still override the next-exam
  // date manually after editing dateExamined.
  const updateSignOff = (key, value) => {
    setFormData((prev) => {
      const signOff = { ...prev.signOff, [key]: value };
      if (key === 'dateExamined') {
        signOff.vesselNextExaminationDate = computeNextExaminationDateIso(value);
      }
      return { ...prev, signOff };
    });
  };

  // Nested setter for a Main Shell grid cell (panel2 / panel4).  `cell` is a
  // numeric shell column index 0..11 -- ENDR / ENDL columns are now driven
  // by the end-cap panels (see updateUltrasonicEndReading) so they are no
  // longer addressed here.
  const updateUltrasonicCell = (gridKey, rowKey, cell, value) => {
    setFormData((prev) => {
      const shellKey = ULTRASONIC_PANEL_CONTROL_MAP[gridKey]
        || ULTRASONIC_ADDITIONAL_PANEL_CONTROL_MAP[gridKey];
      const controlVal = prev.ultrasonic?.controls?.[shellKey];
      if (!Number.isFinite(parseFloat(String(controlVal ?? '').trim()))) return prev;
      const ultrasonic = { ...prev.ultrasonic };
      const grid = { ...ultrasonic[gridKey] };
      const row = { ...grid[rowKey] };
      const shell = [...(row.shell || [])];
      shell[cell] = value;
      row.shell = shell;
      grid[rowKey] = row;
      ultrasonic[gridKey] = grid;
      return { ...prev, ultrasonic };
    });
  };

  // Setter for one of the 6 circular end-cap readings on panel1 / panel3.
  const updateUltrasonicEndReading = (panelKey, idx, value) => {
    setFormData((prev) => {
      const shellKey = ULTRASONIC_PANEL_CONTROL_MAP[panelKey];
      const controlVal = prev.ultrasonic?.controls?.[shellKey];
      if (!Number.isFinite(parseFloat(String(controlVal ?? '').trim()))) return prev;
      const ultrasonic = { ...prev.ultrasonic };
      const panel = { ...(ultrasonic[panelKey] || {}) };
      const readings = [...(panel.readings || [])];
      readings[idx] = value;
      panel.readings = readings;
      ultrasonic[panelKey] = panel;
      return { ...prev, ultrasonic };
    });
  };

  // Per-shell control reading setter.  Each shell (mainShell / endShellRight /
  // endShellLeft) has ONE control reading shared by every panel attached to
  // that shell -- panels 2 and 4 share `mainShell`.
  const updateUltrasonicControl = (shellKey, value) => {
    setFormData((prev) => {
      const ultrasonic = { ...prev.ultrasonic };
      const controls = { ...(ultrasonic.controls || {}), [shellKey]: value };
      ultrasonic.controls = controls;
      return { ...prev, ultrasonic };
    });
  };

  // Enable / disable the optional second main shell (Panels 5 & 6).
  const toggleUltrasonicAdditionalShell = (enabled) => {
    setFormData((prev) => ({
      ...prev,
      ultrasonic: { ...prev.ultrasonic, additionalShell: !!enabled },
    }));
  };

  const handleSerialLookup = useCallback(async () => {
    const serial = String(formData.tank.serialNumber || '').trim();
    if (!serial || serial === serialLookupState.lastLookup) return;

    setSerialLookupState({ loading: true, notFound: false, lastLookup: serial });
    try {
      const result = await lookupVesselBySerial(serial);
      if (!result.found) {
        setSerialLookupState({ loading: false, notFound: true, lastLookup: serial });
        setMissingVesselFields({});
        return;
      }

      const vessel = result.vessel || {};

      if (vessel.owner && !isCalorOwner(vessel.owner)) {
        setWrongOwnerInfo({ serial, owner: vessel.owner });
        setMissingVesselFields({});
        setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
        return;
      }

      const hasManufacturer = !!vessel.vesselManufacturer;
      const hasDesignPressure = vessel.designPressure !== null && vessel.designPressure !== undefined && vessel.designPressure !== '';
      const hasVesselSize = vessel.vesselSize !== null && vessel.vesselSize !== undefined && vessel.vesselSize !== '';
      const hasVesselTypeCode = vessel.vesselTypeCode !== null && vessel.vesselTypeCode !== undefined && vessel.vesselTypeCode !== '';
      const hasDateOfManufacture = !!vessel.dateOfManufacture;

      const missing = {};
      if (!hasManufacturer) missing.manufacturerName = true;
      if (!hasDesignPressure) missing.designPressure = true;
      if (!hasVesselSize) missing.tankSize = true;
      if (!hasDateOfManufacture) missing.manufacturersTestDate = true;

      setFormData((prev) => {
        const tank = { ...prev.tank };
        if (!tank.manufacturerName && hasManufacturer) {
          tank.manufacturerName = String(vessel.vesselManufacturer).toUpperCase();
        }
        if ((tank.designPressure === '' || tank.designPressure === null || tank.designPressure === undefined) && hasDesignPressure) {
          tank.designPressure = String(vessel.designPressure).toUpperCase();
        }
        if ((tank.tankSize === '' || tank.tankSize === null || tank.tankSize === undefined) && hasVesselSize) {
          tank.tankSize = String(vessel.vesselSize).toUpperCase();
        }
        if ((tank.typeCode === '' || tank.typeCode === null || tank.typeCode === undefined) && hasVesselTypeCode) {
          tank.typeCode = String(vessel.vesselTypeCode).toUpperCase();
        }
        if (!tank.manufacturersTestDate && hasDateOfManufacture) {
          const d = new Date(vessel.dateOfManufacture);
          if (!Number.isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            tank.manufacturersTestDate = `${yyyy}-${mm}`;
          }
        }
        // This is a Calor CDE form, so display the Calor serial number in the
        // serial field whenever the vessel has one -- even if the user entered
        // the standard serial number.
        if (vessel.calorSerialNumber) {
          tank.serialNumber = String(vessel.calorSerialNumber).toUpperCase();
        }
        return { ...prev, tank };
      });
      setMissingVesselFields(missing);
      const usingCalorSerial = !!vessel.calorSerialNumber;
      // If we swapped the field to the Calor serial, treat that as the
      // last-looked-up value so the follow-up blur doesn't re-query.
      const lastLookup = usingCalorSerial
        ? String(vessel.calorSerialNumber).toUpperCase()
        : serial;
      setSerialLookupState({
        loading: false,
        notFound: false,
        scrapped: isVesselScrapped(vessel),
        lastLookup,
        usingCalorSerial,
        matchedSerial: vessel.serialNumber || '',
      });
    } catch (error) {
      console.warn('Vessel lookup failed:', error);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
    }
  }, [formData.tank.serialNumber, serialLookupState.lastLookup]);

  // Reasons this can no longer be a certificate (empty => may stay a certificate).
  const certificateDisqualifiers = useMemo(
    () => getCertificateDisqualifiers(formData),
    [formData],
  );
  // Will this document actually be issued as a certificate? Only if the user
  // selected "certificate" AND no disqualifying condition is present.
  const willBeCertificate =
    formData.header.documentType === 'certificate' && certificateDisqualifiers.length === 0;

  // When a torque wrench / pressure gauge is selected, their serial numbers are
  // auto-appended to the comments. In that case a blank free-text comments box
  // is NOT recorded as the "no comments" placeholder -- the equipment lines
  // become the comment content instead.
  const hasEquipmentComment = !!buildEquipmentCommentLines(formData.signOff);

  // Everything on the form is mandatory EXCEPT the ultrasonic panel readings,
  // the materials-used table and the free-text comments box. Control readings
  // for the three default shells (and Main Shell 2 when added) are required.
  const validationErrors = useMemo(() => {
    const errs = [];
    const h = formData.header;
    const t = formData.tank;
    const rv = formData.reliefValve;
    const c = formData.checklist;
    const s = formData.signOff;
    const str = (v) => String(v ?? '').trim();
    const controlTaken = (v) => Number.isFinite(parseFloat(str(v)));

    // Header
    if (!h.documentType) errs.push('Document Type');
    if (!str(h.customerName)) errs.push('Depot Name');
    if (!str(h.installationAddress)) errs.push('Depot Address');
    if (!str(h.deliveryPointCode)) errs.push('Delivery Point Code');
    if (!str(h.certificateNumber)) errs.push('Certificate Number');
    if (!str(h.wseReference)) errs.push('WSE Reference');
    if (!h.examinationType) errs.push('Examination Type');
    if (!h.installationCategory) errs.push('Installation Category');
    if (!h.vesselType) errs.push('Vessel Type');
    if (!h.installationUse) errs.push('Installation Use');

    // Tank
    if (!str(t.serialNumber)) errs.push('Tank Serial Number');
    if (!str(t.nameplateTestCode)) errs.push('Nameplate / Test Code');
    if (!str(t.typeCode)) errs.push('Type Code');
    if (!str(t.manufacturersCode)) errs.push("Manufacturer's Code");
    if (!str(t.manufacturerName)) errs.push('Manufacturer Name');
    if (!str(t.manufacturersTestDate)) errs.push("Manufacturer's Test Date");
    if (!str(t.previousInserviceDateCode)) errs.push('Previous In-Service Date Code');
    if (!str(t.previousThoroughDateCode)) errs.push('Previous Thorough Date Code');
    if (!str(t.tankSize)) errs.push('Tank Size');
    if (!str(t.designCode)) errs.push('Design Code');
    if (!str(t.designPressure)) errs.push('Design Pressure');
    if (!t.colour) errs.push('Colour');
    if (!t.useVap && !t.useTopLiq && !t.useBottomLiq) errs.push('Tank Use (select at least one)');

    // Relief valve
    if (!str(rv.manufacturer)) errs.push('Relief Valve Manufacturer');
    if (!str(rv.type)) errs.push('Relief Valve Type');
    if (!str(rv.setPressure)) errs.push('Relief Valve Set Pressure');
    if (!str(rv.testDates)) errs.push('Relief Valve Test Dates');

    // Checklist
    if (!c.corrosionState) errs.push('Tank Corrosion State');
    if (!c.hydraulicTest) errs.push('Hydraulic Test');
    if (c.hydraulicTest === 'yes' && !str(c.hydraulicTestPressure)) errs.push('Hydraulic Test Pressure');
    if (!c.paintworkMeetsRequirements) errs.push('Paintwork Meets Requirements');
    if (c.paintworkMeetsRequirements === 'no' && !c.paintworkRequiresRepaint && !c.paintworkRequiresCleaning) {
      errs.push('Tank Paintwork Requires (Repaint or Cleaning)');
    }

    // Sign off
    if (!str(s.employerName)) errs.push('Employer Name');
    if (!str(s.inspectorName)) errs.push('Inspector Name');
    if (!s.dateExamined) errs.push('Date Examined');
    if (!str(s.inspectorCertificateNumber)) errs.push('Inspector Certificate Number');
    // Signature is only meaningful for an actual certificate. Reports and
    // inspections (including downgraded ones) print "This is not a certificate".
    if (willBeCertificate && !s.inspectorSignature) errs.push('Inspector Signature');
    if (!s.additionalExamination) errs.push('Additional Examination Required');
    if (!s.vesselSatisfactory) errs.push('Vessel Satisfactory');
    if (!s.repairsRequired) errs.push('Repairs Required');
    if (!s.changesSafeLimits) errs.push('Changes to Safe Operating Limits');
    if (!s.wseSuitable) errs.push('WSE Suitable');
    if (!s.vesselNextExaminationDate) errs.push('Next Examination Date');
    if (!s.upliftRequired) errs.push('Uplift Required');
    if (!s.torqueWrench) errs.push('Torque Wrench Used');
    if (!s.pressureGauge) errs.push('Pressure Gauge Used');

    const controls = formData.ultrasonic?.controls || {};
    ULTRASONIC_SHELL_KEYS.forEach((shellKey) => {
      if (!controlTaken(controls[shellKey])) {
        errs.push(`${ULTRASONIC_SHELL_LABELS[shellKey]} Control reading`);
      }
    });
    if (formData.ultrasonic?.additionalShell && !controlTaken(controls.mainShell2)) {
      errs.push('Main Shell 2 Control reading');
    }

    return errs;
  }, [formData, willBeCertificate]);

  // Hard rule: relief valve set pressure must NOT exceed the tank's design
  // pressure.  Compared as numbers (free-text "17.25 bar" is parsed).  If
  // either field is empty/non-numeric we don't trigger -- the rule only
  // fires when both values are present and in violation.
  const designPressureNumber = parsePressure(formData.tank.designPressure);
  const reliefSetPressureNumber = parsePressure(formData.reliefValve.setPressure);
  const reliefValveExceedsDesign = (
    Number.isFinite(designPressureNumber)
    && Number.isFinite(reliefSetPressureNumber)
    && reliefSetPressureNumber > designPressureNumber
  );

  // Open the warning modal the moment the rule transitions from OK -> broken
  // so the inspector sees it as soon as they enter the offending value.
  useEffect(() => {
    if (reliefValveExceedsDesign && !prevExceedsRef.current) {
      setPressureWarningOpen(true);
    }
    prevExceedsRef.current = reliefValveExceedsDesign;
  }, [reliefValveExceedsDesign]);

  // Per-shell ±10% control rule.  Each panel is mapped to its shell's
  // control via ULTRASONIC_PANEL_CONTROL_MAP; panels 2 and 4 are both checked
  // against the single mainShell control.  Empty cells and panels whose
  // shell has no numeric control are skipped.  Returns a flat list of
  // human-readable violation rows for the warning modal.
  //
  // Two panel shapes:
  //   - end-shell (panel1, panel3): { readings: [v0..v5] }
  //   - main-shell grid (panel2, panel4): { rowKey: { shell: [c0..c11] } }
  const ultrasonicViolations = useMemo(() => {
    const violations = [];
    const controls = formData.ultrasonic?.controls || {};
    const additionalShell = !!formData.ultrasonic?.additionalShell;
    const panelKeys = additionalShell
      ? [...ULTRASONIC_PANEL_KEYS, ...ULTRASONIC_ADDITIONAL_PANEL_KEYS]
      : ULTRASONIC_PANEL_KEYS;
    const controlMap = { ...ULTRASONIC_PANEL_CONTROL_MAP, ...ULTRASONIC_ADDITIONAL_PANEL_CONTROL_MAP };
    const panelLabel = (panelKey) =>
      ULTRASONIC_GRID_LABELS[panelKey] || ULTRASONIC_ADDITIONAL_GRID_LABELS[panelKey] || panelKey;
    const pushIfOut = (panelKey, shellKey, controlNum, label, val) => {
      if (val === '' || val === null || val === undefined) return;
      const num = parseFloat(val);
      if (!Number.isFinite(num)) return;
      const lower = controlNum * (1 - ULTRASONIC_CONTROL_TOLERANCE);
      const upper = controlNum * (1 + ULTRASONIC_CONTROL_TOLERANCE);
      if (num < lower || num > upper) {
        violations.push({
          panel: panelLabel(panelKey),
          shell: ULTRASONIC_SHELL_LABELS[shellKey] || shellKey,
          cell: label,
          value: num,
          control: controlNum,
          lower,
          upper,
        });
      }
    };
    for (const panelKey of panelKeys) {
      const panel = formData.ultrasonic?.[panelKey];
      if (!panel) continue;
      const shellKey = controlMap[panelKey];
      const controlNum = parseFloat(controls[shellKey]);
      if (!Number.isFinite(controlNum)) continue;
      if (ULTRASONIC_END_PANEL_KEYS.includes(panelKey)) {
        const readings = Array.isArray(panel.readings) ? panel.readings : [];
        readings.forEach((val, idx) => {
          pushIfOut(panelKey, shellKey, controlNum, END_READING_LABEL(idx), val);
        });
      } else {
        for (const rowKey of ULTRASONIC_ROW_KEYS) {
          const row = panel[rowKey] || {};
          const shellCells = (row.shell || []).slice(0, SHELL_COL_COUNT);
          shellCells.forEach((val, i) => {
            const label = `${ULTRASONIC_ROW_LABELS[rowKey]}, Col ${i + 1}`;
            pushIfOut(panelKey, shellKey, controlNum, label, val);
          });
        }
      }
    }
    return violations;
  }, [formData.ultrasonic]);

  // Even-coverage rule for the main shells: all six angle rows on a shell
  // (front and back, TDC 30 / 90 / BDC 20) should carry the same number of
  // readings. Rows that differ from the shell's baseline count are reported so
  // the inspector either amends them or explains why in the comments. A shell
  // with no readings at all is skipped -- nothing has been recorded yet.
  const readingCountIssues = useMemo(() => {
    const groups = [{ shell: 'Main Shell', panels: ULTRASONIC_SHELL_PANELS.mainShell || [] }];
    if (formData.ultrasonic?.additionalShell) {
      groups.push({ shell: 'Main Shell 2 (Additional)', panels: ULTRASONIC_ADDITIONAL_PANEL_KEYS });
    }
    const issues = [];
    for (const group of groups) {
      const rows = [];
      for (const panelKey of group.panels) {
        const panelLabel = MAIN_SHELL_PANEL_SHORT_LABELS[panelKey] || panelKey;
        for (const rowKey of ULTRASONIC_ROW_KEYS) {
          const cells = formData.ultrasonic?.[panelKey]?.[rowKey]?.shell || [];
          const count = cells.filter((cell) => String(cell ?? '').trim() !== '').length;
          rows.push({ angle: `${ULTRASONIC_ROW_LABELS[rowKey]} on ${panelLabel}`, count });
        }
      }
      if (rows.every((row) => row.count === 0)) continue;
      const baseline = baselineReadingCount(rows.map((row) => row.count));
      rows.forEach((row) => {
        if (row.count === baseline) return;
        issues.push({ shell: group.shell, angle: row.angle, count: row.count, baseline });
      });
    }
    return issues;
  }, [formData.ultrasonic]);

  // Shared-row capacity: on the PDF the second main shell continues on the
  // same 12-column rows as Panels 2/4, separated by one N/A column. So the
  // combined readings per row (front: 2+5, back: 4+6) must fit in 11 cells.
  const ultrasonicCapacityErrors = useMemo(() => {
    if (!formData.ultrasonic?.additionalShell) return [];
    const compactCount = (arr) => {
      const a = Array.isArray(arr) ? arr : [];
      let end = a.length;
      while (end > 0 && String(a[end - 1] ?? '').trim() === '') end--;
      return end;
    };
    const errs = [];
    const pairs = [
      { label: 'Front (Panels 2 + 5)', primary: 'panel2', secondary: 'panel5' },
      { label: 'Back (Panels 4 + 6)', primary: 'panel4', secondary: 'panel6' },
    ];
    for (const { label, primary, secondary } of pairs) {
      for (const rowKey of ULTRASONIC_ROW_KEYS) {
        const p = compactCount(formData.ultrasonic?.[primary]?.[rowKey]?.shell);
        const s = compactCount(formData.ultrasonic?.[secondary]?.[rowKey]?.shell);
        if (p + s > ULTRASONIC_SHARED_ROW_CAPACITY) {
          errs.push(
            `${label} ${ULTRASONIC_ROW_LABELS[rowKey]}: ${p} + ${s} = ${p + s} readings exceed the ${ULTRASONIC_SHARED_ROW_CAPACITY}-cell shared row.`,
          );
        }
      }
    }
    return errs;
  }, [formData.ultrasonic]);

  // Build the request payload for either submission path.  When the inspector
  // proceeds past the ultrasonic-violation modal the document is recorded as
  // a report (used as evidence) -- for that one request only, the form's own
  // documentType is left untouched.
  const buildPayload = (overrides = {}) => {
    const payload = { ...formData, materials };
    // Guard the printed capitals for values that never passed through the
    // section setters (restored drafts, vessel pre-fill).
    payload.tank = upperCaseTextFields(payload.tank, TANK_TEXT_KEYS);
    payload.reliefValve = upperCaseTextFields(payload.reliefValve, RELIEF_VALVE_TEXT_KEYS);
    payload.reliefValve.setPressure = sanitizeSetPressure(payload.reliefValve.setPressure);
    // Resolve the effective document type: an explicit downgrade choice wins,
    // then the legacy ultrasonic "submit as report" path, else the form value.
    const documentType =
      overrides.forceDocumentType
      || (overrides.submitAsReport ? 'report' : payload.header.documentType);
    payload.header = { ...payload.header, documentType };
    // Downgrade explanation (from the failsafe modal) becomes the comments.
    if (overrides.commentsExplanation && overrides.commentsExplanation.trim()) {
      payload.signOff = { ...payload.signOff, comments: overrides.commentsExplanation.trim() };
    }
    // Uneven-reading explanation. Carried through the overrides because the
    // state update that adds it to the comments box hasn't landed yet.
    if (overrides.appendComment && overrides.appendComment.trim()) {
      const line = overrides.appendComment.trim();
      const existing = String(payload.signOff.comments || '').trim();
      if (!existing.includes(line)) {
        payload.signOff = {
          ...payload.signOff,
          comments: existing ? `${existing}\n${line}` : line,
        };
      }
    }
    // Append equipment traceability (torque wrench / pressure gauge serials) so
    // it always prints in the certificate comments section.
    const equipmentLines = buildEquipmentCommentLines(payload.signOff);
    if (equipmentLines) {
      const existing = String(payload.signOff.comments || '').trim();
      payload.signOff = {
        ...payload.signOff,
        comments: existing ? `${existing}\n${equipmentLines}` : equipmentLines,
      };
    }
    // Substitute the confirmed-no-comments placeholder when the field is blank.
    // The comments guard ensures the inspector has already confirmed this.
    if (!String(payload.signOff.comments || '').trim()) {
      payload.signOff = { ...payload.signOff, comments: NO_COMMENTS_PLACEHOLDER };
    }
    // Non-certificates carry no signature; the backend prints the notice.
    if (documentType !== 'certificate') {
      payload.signOff = { ...payload.signOff, inspectorSignature: '' };
    }
    // Dates print in UK format (DD/MM/YYYY) on the certificate.
    payload.signOff = {
      ...payload.signOff,
      dateExamined: formatIsoToDDMMYYYY(payload.signOff.dateExamined),
      vesselNextExaminationDate: formatIsoToDDMMYYYY(payload.signOff.vesselNextExaminationDate),
    };
    return payload;
  };

  // Returns true (and opens the confirmation modal) when comments are blank and
  // the inspector hasn't yet confirmed. `resume` is invoked once they confirm.
  const blockedByBlankComments = (options, resume) => {
    // Downgrade path already captured a mandatory explanation.
    if (options.certificateDowngradeHandled) return false;
    const commentsBlank = !String(formData.signOff.comments || '').trim();
    if (commentsBlank && !options.commentsConfirmed) {
      pendingCommentsActionRef.current = resume;
      setCommentsWarningOpen(true);
      return true;
    }
    return false;
  };

  // Uneven main-shell reading counts: the inspector must either go back and
  // amend the readings or record why the counts differ.
  const blockedByReadingCounts = (options, resume) => {
    if (options.readingCountsExplained) return false;
    if (readingCountIssues.length === 0) return false;
    setReadingCountsExplanation('');
    setReadingCountsError('');
    pendingReadingCountsActionRef.current = resume;
    setUltrasonicWarningOpen(false);
    setReadingCountsOpen(true);
    return true;
  };

  // Failsafe: if the user is trying to issue a certificate but a disqualifying
  // condition is present, block and force a downgrade to Report/Inspection with
  // a mandatory explanation. Skipped when the user already picked report/
  // inspection, or once the downgrade has been handled for this action.
  const blockedByCertificateDowngrade = (options, resume) => {
    if (options.certificateDowngradeHandled) return false;
    if (formData.header.documentType !== 'certificate') return false;
    if (certificateDisqualifiers.length === 0) return false;
    setDowngradeChoice('report');
    setDowngradeExplanation(String(formData.signOff.comments || ''));
    setDowngradeError('');
    pendingDowngradeActionRef.current = resume;
    setDowngradeOpen(true);
    return true;
  };

  const blockedByPreflight = (action) => {
    if (reliefValveExceedsDesign) {
      setPressureWarningOpen(true);
      setErrorMsg(`Relief valve set pressure exceeds tank design pressure. Resolve the discrepancy before ${action}.`);
      return true;
    }
    if (validationErrors.length > 0) {
      setErrorMsg(`Please complete required fields: ${validationErrors.join(', ')}`);
      return true;
    }
    if (ultrasonicCapacityErrors.length > 0) {
      setErrorMsg(`Additional shell readings don't fit the certificate rows. ${ultrasonicCapacityErrors.join(' ')}`);
      return true;
    }
    return false;
  };

  const handleSubmit = async (e, options = {}) => {
    if (e?.preventDefault) e.preventDefault();
    setErrorMsg('');
    if (blockedByPreflight('submitting')) return;
    if (ultrasonicViolations.length > 0 && !options.submitAsReport) {
      setUltrasonicWarningOpen(true);
      return;
    }
    if (blockedByReadingCounts(options, (extra) => handleSubmit(null, { ...options, ...extra }))) {
      return;
    }
    if (blockedByCertificateDowngrade(options, (extra) => handleSubmit(null, { ...options, ...extra }))) {
      return;
    }
    if (blockedByBlankComments(options, () => handleSubmit(null, { ...options, commentsConfirmed: true }))) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await generateCalorCdePdf(buildPayload(options), { sendEmail: true });
      if (result.emailStatus !== 'sent') {
        throw new Error('Certificate generated but email delivery failed. Please contact support.');
      }
      setUltrasonicWarningOpen(false);
      clearDraftStorage();
      setSuccessOpen(true);
    } catch (err) {
      console.error('Calor CDE submit failed:', err);
      setErrorMsg(err.message || 'Something went wrong submitting the form.');
    } finally {
      setSubmitting(false);
    }
  };

  // Record the mandatory explanation for the uneven reading counts in the
  // comments section, then resume the original submit.
  const confirmReadingCounts = () => {
    const explanation = readingCountsExplanation.trim();
    if (!explanation) {
      setReadingCountsError('Explain why the reading counts differ, or go back and amend the readings.');
      return;
    }
    const line = `${UNEVEN_READINGS_COMMENT_PREFIX}${explanation}`;
    setFormData((prev) => {
      const existing = String(prev.signOff.comments || '').trim();
      return {
        ...prev,
        signOff: { ...prev.signOff, comments: existing ? `${existing}\n${line}` : line },
      };
    });
    setReadingCountsOpen(false);
    const resume = pendingReadingCountsActionRef.current;
    pendingReadingCountsActionRef.current = null;
    if (resume) resume({ readingCountsExplained: true, appendComment: line });
  };

  // Confirm the certificate -> report/inspection downgrade: record the choice
  // and mandatory explanation, then resume the original submit.
  const confirmDowngrade = () => {
    const explanation = downgradeExplanation.trim();
    if (!explanation) {
      setDowngradeError('Please explain why this can no longer be a certificate.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      header: { ...prev.header, documentType: downgradeChoice },
      signOff: { ...prev.signOff, comments: explanation },
    }));
    setDowngradeOpen(false);
    const resume = pendingDowngradeActionRef.current;
    pendingDowngradeActionRef.current = null;
    if (resume) {
      resume({
        certificateDowngradeHandled: true,
        forceDocumentType: downgradeChoice,
        commentsExplanation: explanation,
      });
    }
  };

  return (
    <PVSPageLayout className="p-6 md:p-8">
      {certificateLoading ? (
        <div className="max-w-5xl mx-auto pvs-glass border border-pvs-border rounded-xl p-8 text-center text-pvs-text-muted">
          Loading certificate number allocation…
        </div>
      ) : !certificateBlocked ? (
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">CDE Vessel Certification</h1>
            <p className="text-pvs-text-muted text-sm mt-1">
              Calor Gas Bulk Tank Periodic Examination (CGL104228)
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => setClearConfirmOpen(true)}
              className="px-4 h-12 rounded-lg border border-pvs-border text-white hover:bg-white/5"
              title="Clear all entered content"
            >
              Clear Form
            </button>
            <button
              type="button"
              onClick={onBack}
              className="flex items-center justify-center w-12 h-12 pvs-glass pvs-glass-hover"
              title="Back"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>
        </div>

        {numberingDisabledForTesting && (
          <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-900/20 px-4 py-3 text-sm text-amber-200">
            Certificate numbering is disabled for testing. Enter a certificate number manually before submitting.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-0">
          <HeaderSection
            data={formData.header}
            update={updateSection('header')}
            certificateNumberReadOnly={!numberingDisabledForTesting}
          />
          <TankDetailsSection
            data={formData.tank}
            update={updateSection('tank')}
            onSerialLookup={handleSerialLookup}
            serialLookupState={serialLookupState}
            missingVesselFields={missingVesselFields}
            onOpenLineOfContact={onOpenLineOfContact}
          />
          <UltrasonicSection
            data={formData.ultrasonic}
            updateCell={updateUltrasonicCell}
            updateControl={updateUltrasonicControl}
            updateEndReading={updateUltrasonicEndReading}
            toggleAdditionalShell={toggleUltrasonicAdditionalShell}
          />
          <ReliefValveSection data={formData.reliefValve} update={updateSection('reliefValve')} />
          <ChecklistSection
            data={formData.checklist}
            update={updateSection('checklist')}
            conditionalVisibility={conditionalChecklistVisibility}
          />
          <SignOffSection
            data={formData.signOff}
            update={updateSignOff}
            documentType={formData.header.documentType}
            certificateDisqualifiers={certificateDisqualifiers}
            torqueWrenchOptions={TORQUE_WRENCH_OPTIONS}
            pressureGaugeOptions={PRESSURE_GAUGE_OPTIONS}
            dateExaminedDisplay={formatIsoToDDMMYYYY(formData.signOff.dateExamined)}
            nextExaminationDateDisplay={formatIsoToDDMMYYYY(formData.signOff.vesselNextExaminationDate)}
          />
          <MaterialsSection rows={materials} update={updateMaterial} />

          {errorMsg && (
            <div className="pvs-glass border border-red-500/40 bg-red-900/20 p-4 mb-4 rounded-lg">
              <p className="text-red-300 text-sm">{errorMsg}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-end pb-10">
            <button
              type="submit"
              disabled={submitting || reliefValveExceedsDesign}
              className="px-5 py-3 rounded-lg bg-pvs-accent text-pvs-bg font-semibold hover:bg-pvs-accent-dim disabled:opacity-50 disabled:cursor-not-allowed"
              title={reliefValveExceedsDesign ? 'Relief valve set pressure exceeds tank design pressure' : undefined}
            >
              {submitting ? 'Sending…' : 'Submit & Email'}
            </button>
          </div>
        </form>
      </div>
      ) : null}

      <FormSuccessModal
        isOpen={successOpen}
        formName="Calor CDE Certification"
        onClose={() => {
          setSuccessOpen(false);
          onBack?.();
        }}
      />

      {certificateBlocked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-70" />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-white flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-white text-lg font-bold">Certificate Numbers Unavailable</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-white text-base font-semibold leading-relaxed">
                {certificateBlocked}
              </p>
            </div>
            <div className="px-6 pb-5 flex justify-end">
              <button
                type="button"
                onClick={() => onBack?.()}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Back to Calor Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {wrongOwnerInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => {
              setWrongOwnerInfo(null);
              onBack?.();
            }}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-white flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-white text-lg font-bold">Not a Calor Gas Vessel</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-white text-base font-semibold leading-relaxed">
                This is not a Calor Gas vessel. Please use the Tank Certification form.
              </p>
              <p className="text-pvs-text-dim text-sm mt-3">
                Serial <span className="text-white font-medium">{wrongOwnerInfo.serial}</span> is registered to{' '}
                <span className="text-white font-medium">{wrongOwnerInfo.owner}</span> in the Vessel Inventory.
              </p>
              <p className="text-pvs-text-dim text-xs mt-3 italic">
                You cannot continue this form for a non-Calor vessel. Closing will exit back to the Calor menu.
              </p>
            </div>
            <div className="px-6 pb-5 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setWrongOwnerInfo(null);
                  onBack?.();
                }}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Close
              </button>
              {onOpenTankCertification && tankCertificationUnlocked && (
                <button
                  type="button"
                  onClick={() => {
                    setWrongOwnerInfo(null);
                    onOpenTankCertification();
                  }}
                  className="px-6 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg shadow hover:bg-pvs-accent-dim underline"
                >
                  Open Tank Certification Form
                </button>
              )}
              {onOpenTankCertification && !tankCertificationUnlocked && (
                <p className="text-sm text-amber-300 sm:col-span-2">
                  Tank Certification is locked until today&apos;s Ultrasonic Daily Check is completed.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {pressureWarningOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => setPressureWarningOpen(false)}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-white flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-white text-lg font-bold">Relief Valve Pressure Exceeds Design Pressure</h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-white text-base font-semibold leading-relaxed">
                The relief valve set pressure must not exceed the tank&apos;s design pressure.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="pvs-glass border border-pvs-border rounded-lg p-3">
                  <div className="text-pvs-text-dim text-xs uppercase tracking-wide">Design Pressure</div>
                  <div className="text-white text-lg font-semibold mt-1">
                    {Number.isFinite(designPressureNumber) ? designPressureNumber : '—'}
                  </div>
                </div>
                <div className="pvs-glass border border-red-500/40 bg-red-900/20 rounded-lg p-3">
                  <div className="text-red-300 text-xs uppercase tracking-wide">Relief Valve Set Pressure</div>
                  <div className="text-red-200 text-lg font-semibold mt-1">
                    {Number.isFinite(reliefSetPressureNumber) ? reliefSetPressureNumber : '—'}
                  </div>
                </div>
              </div>
              <p className="text-pvs-text-dim text-xs italic">
                Submission is blocked until either Relief Valve Set Pressure is reduced
                or Tank Design Pressure is corrected.
              </p>
            </div>
            <div className="px-6 pb-5 flex justify-end">
              <button
                type="button"
                onClick={() => setPressureWarningOpen(false)}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {ultrasonicWarningOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => setUltrasonicWarningOpen(false)}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-white flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-white text-lg font-bold">
                Ultrasonic readings outside ±10% of control
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-white text-sm">
                {ultrasonicViolations.length} reading{ultrasonicViolations.length === 1 ? '' : 's'} fall
                outside the ±{Math.round(ULTRASONIC_CONTROL_TOLERANCE * 100)}% tolerance of the panel control:
              </p>
              <div className="pvs-glass border border-pvs-border rounded-lg divide-y divide-pvs-border/60">
                {ultrasonicViolations.map((v, i) => (
                  <div key={i} className="px-4 py-2.5 text-xs text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <div className="font-semibold">{v.panel}</div>
                    <div className="text-pvs-text-muted">{v.cell}</div>
                    <div>
                      <span className="text-red-300 font-semibold">{v.value.toFixed(2)}</span>
                      <span className="text-pvs-text-dim"> (allowed {v.lower.toFixed(2)} – {v.upper.toFixed(2)}, control {v.control.toFixed(2)})</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-pvs-border/60 pt-3">
                <p className="text-pvs-text-muted text-xs leading-relaxed">
                  {ULTRASONIC_FAILURE_GUIDANCE}
                </p>
              </div>
            </div>
            <div className="px-6 pb-5 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setUltrasonicWarningOpen(false)}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Cancel & Fix
              </button>
              <button
                type="button"
                onClick={() => handleSubmit(null, { submitAsReport: true })}
                disabled={submitting}
                className="px-5 py-2.5 bg-amber-500 text-pvs-bg font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50"
              >
                {submitting ? 'Sending…' : 'Submit as Report (use as evidence)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {readingCountsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => {
              setReadingCountsOpen(false);
              pendingReadingCountsActionRef.current = null;
            }}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-pvs-bg flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-pvs-bg text-lg font-bold">Uneven Readings Across Angles</h2>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-white text-sm font-semibold leading-relaxed">
                Every angle on a main shell should carry the same number of readings.
              </p>
              <div className="pvs-glass border border-amber-500/40 bg-amber-900/20 rounded-lg divide-y divide-amber-500/20">
                {readingCountIssues.map((issue, i) => (
                  <div key={i} className="px-4 py-2.5">
                    <div className="text-amber-300 text-xs uppercase tracking-wide mb-0.5">{issue.shell}</div>
                    <div className="text-white text-sm">{describeReadingCountIssue(issue)}</div>
                  </div>
                ))}
              </div>
              <p className="text-pvs-text-muted text-sm">
                Go back and amend the readings, or explain below why the counts differ.
                Your explanation is recorded in the comments section of the certificate.
              </p>
              <div>
                <span className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Explanation <span className="text-red-400">*</span>
                </span>
                <textarea
                  value={readingCountsExplanation}
                  onChange={(e) => {
                    setReadingCountsExplanation(e.target.value);
                    if (readingCountsError) setReadingCountsError('');
                  }}
                  rows={3}
                  className="pvs-input w-full"
                  placeholder="Explain why the number of readings differs at these angles…"
                />
                {readingCountsError && (
                  <p className="text-red-300 text-xs mt-1">{readingCountsError}</p>
                )}
              </div>
            </div>
            <div className="px-6 pb-5 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setReadingCountsOpen(false);
                  pendingReadingCountsActionRef.current = null;
                }}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Amend Readings
              </button>
              <button
                type="button"
                onClick={confirmReadingCounts}
                className="px-5 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim"
              >
                Save Comment &amp; Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {commentsWarningOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => {
              setCommentsWarningOpen(false);
              pendingCommentsActionRef.current = null;
            }}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-pvs-bg flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-pvs-bg text-lg font-bold">Comments Section Left Blank</h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-white text-base font-semibold leading-relaxed">
                The comments section has been left blank. Are you sure you want to proceed?
              </p>
              {hasEquipmentComment ? (
                <p className="text-pvs-text-dim text-sm">
                  The selected equipment serial numbers (torque wrench / pressure gauge)
                  will still be recorded automatically in the comments section.
                </p>
              ) : (
                <p className="text-pvs-text-dim text-sm">
                  If you continue, the comments will be recorded as
                  &ldquo;{NO_COMMENTS_PLACEHOLDER}&rdquo;.
                </p>
              )}
            </div>
            <div className="px-6 pb-5 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setCommentsWarningOpen(false);
                  pendingCommentsActionRef.current = null;
                }}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Go Back &amp; Add Comments
              </button>
              <button
                type="button"
                onClick={() => {
                  setCommentsWarningOpen(false);
                  const resume = pendingCommentsActionRef.current;
                  pendingCommentsActionRef.current = null;
                  if (resume) resume();
                }}
                className="px-5 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim"
              >
                Yes, Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {downgradeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => {
              setDowngradeOpen(false);
              pendingDowngradeActionRef.current = null;
            }}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-white flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-white text-lg font-bold">This Can No Longer Be a Certificate</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-white text-sm font-semibold leading-relaxed">
                One or more conditions mean this examination cannot be issued as a certificate.
                It must be recorded as a Report or Inspection instead — no signature will be
                applied and the PDF will state &ldquo;This is not a certificate&rdquo;.
              </p>
              <div className="pvs-glass border border-red-500/40 bg-red-900/20 rounded-lg p-3">
                <div className="text-red-300 text-xs uppercase tracking-wide mb-1">Reasons</div>
                <ul className="list-disc list-inside text-red-200 text-sm space-y-0.5">
                  {certificateDisqualifiers.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="block text-sm font-medium text-pvs-text-muted mb-2">Record this document as</span>
                <div className="flex gap-3">
                  {[
                    { value: 'report', label: 'Report' },
                    { value: 'inspection', label: 'Inspection' },
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="downgradeChoice"
                        value={opt.value}
                        checked={downgradeChoice === opt.value}
                        onChange={(e) => setDowngradeChoice(e.target.value)}
                        className="accent-pvs-accent"
                      />
                      <span className="text-sm text-white">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <span className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Explanation <span className="text-red-400">*</span>
                </span>
                <textarea
                  value={downgradeExplanation}
                  onChange={(e) => {
                    setDowngradeExplanation(e.target.value);
                    if (downgradeError) setDowngradeError('');
                  }}
                  rows={3}
                  className="pvs-input w-full"
                  placeholder="Explain why this can no longer be issued as a certificate…"
                />
                {downgradeError && (
                  <p className="text-red-300 text-xs mt-1">{downgradeError}</p>
                )}
              </div>
            </div>
            <div className="px-6 pb-5 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setDowngradeOpen(false);
                  pendingDowngradeActionRef.current = null;
                }}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={confirmDowngrade}
                className="px-5 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim"
              >
                Confirm &amp; Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {schneiderPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => setSchneiderPrompt(null)}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-pvs-accent px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-pvs-bg flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <h2 className="text-pvs-bg text-lg font-bold">Confirm Manufacturer</h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-white text-base font-semibold leading-relaxed">
                Which Schneider manufacturer is this vessel?
              </p>
              <p className="text-pvs-text-dim text-sm">
                Schneider has two entries with different Calor codes. Select the correct
                one so the Manufacturer&apos;s Code is populated accurately.
              </p>
            </div>
            <div className="px-6 pb-5 flex flex-col gap-3">
              {schneiderPrompt.options.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => handleSchneiderChoice(option)}
                  className="w-full px-4 py-3 rounded-lg border border-pvs-border text-white hover:bg-white/5 flex items-center justify-between gap-3"
                >
                  <span className="font-semibold">{option.label}</span>
                  <span className="text-pvs-text-dim text-sm">Code {option.code}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {clearConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-70"
            onClick={() => setClearConfirmOpen(false)}
          />
          <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
              <svg className="h-7 w-7 text-pvs-bg flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-pvs-bg text-lg font-bold">Clear Form?</h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-white text-base font-semibold leading-relaxed">
                This will permanently remove all content you&apos;ve entered on this form.
              </p>
              <p className="text-pvs-text-dim text-sm">
                The saved draft will also be discarded. This can&apos;t be undone.
              </p>
            </div>
            <div className="px-6 pb-5 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setClearConfirmOpen(false)}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClearForm}
                className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-500"
              >
                Yes, Clear Form
              </button>
            </div>
          </div>
        </div>
      )}
    </PVSPageLayout>
  );
};

export default CalorCDEForm;
